import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { categoryServices } from '../services/category';
import { foodServices } from '../services/food/food';
import tagService from '../services/tag';

const initialState = {
    categories: {},
    items: [],
    tags: [],
    loading: false,
    error: null,
    lastUpdated: null,
};

// Async thunk for fetching complete menu (categories, foods, tags)
export const fetchMenu = createAsyncThunk(
    'menu/fetchMenu',
    async (_, { rejectWithValue }) => {
        try {
            // Fetch all menu data in parallel
            const [categoriesResponse, foodsResponse, tagsResponse] = await Promise.all([
                categoryServices.getCategories(true),
                foodServices.getFoods(true),
                tagService.getTags(true),
            ]);

            // Check if all requests succeeded
            if (categoriesResponse.status !== 200) {
                return rejectWithValue({
                    status: categoriesResponse.status,
                    message: categoriesResponse.data?.message || 'Failed to fetch categories',
                });
            }
            if (foodsResponse.status !== 200) {
                return rejectWithValue({
                    status: foodsResponse.status,
                    message: foodsResponse.data?.message || 'Failed to fetch foods',
                });
            }
            if (tagsResponse.status !== 200) {
                return rejectWithValue({
                    status: tagsResponse.status,
                    message: tagsResponse.data?.message || 'Failed to fetch tags',
                });
            }

            return {
                categories: categoriesResponse.data,
                items: foodsResponse.data,
                tags: tagsResponse.data,
            };
        } catch (error) {
            return rejectWithValue({
                status: error.response?.status || 500,
                message: error.response?.data?.message || error.message || 'Network error',
            });
        }
    },
);

// Async thunk for fetching only categories
export const fetchCategories = createAsyncThunk(
    'menu/fetchCategories',
    async (_, { rejectWithValue }) => {
        try {
            const response = await categoryServices.getCategories(true);
            if (response.status === 200) {
                return response.data;
            }
            return rejectWithValue({
                status: response.status,
                message: response.data?.message || 'Failed to fetch categories',
            });
        } catch (error) {
            return rejectWithValue({
                status: error.response?.status || 500,
                message: error.response?.data?.message || error.message || 'Network error',
            });
        }
    },
);

export const menuSlice = createSlice({
    name: 'menu',
    initialState,
    reducers: {
        setCategories: (state, action) => {
            state.categories = action.payload;
            state.lastUpdated = new Date().toISOString();
        },
        setItems: (state, action) => {
            state.items = action.payload;
            state.lastUpdated = new Date().toISOString();
        },
        setTags: (state, action) => {
            state.tags = action.payload;
        },
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        clearMenu: (state) => {
            state.categories = {};
            state.items = [];
            state.tags = [];
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // fetchMenu pending
            .addCase(fetchMenu.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            // fetchMenu fulfilled
            .addCase(fetchMenu.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.categories = action.payload.categories;
                state.items = action.payload.items;
                state.tags = action.payload.tags;
                state.lastUpdated = new Date().toISOString();
            })
            // fetchMenu rejected
            .addCase(fetchMenu.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || {
                    status: 500,
                    message: 'Failed to fetch menu data',
                };
            })
            // fetchCategories pending
            .addCase(fetchCategories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            // fetchCategories fulfilled
            .addCase(fetchCategories.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.categories = action.payload;
                state.lastUpdated = new Date().toISOString();
            })
            // fetchCategories rejected
            .addCase(fetchCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || {
                    status: 500,
                    message: 'Failed to fetch categories',
                };
            });
    },
});

// Action creators
export const {
    setCategories,
    setItems,
    setTags,
    setLoading,
    setError,
    clearError,
    clearMenu,
} = menuSlice.actions;

// Selectors
export const selectMenu = (state) => state.menu;
export const selectCategories = (state) => state.menu.categories;
export const selectMenuItems = (state) => state.menu.items;
export const selectTags = (state) => state.menu.tags;
export const selectMenuLoading = (state) => state.menu.loading;
export const selectMenuError = (state) => state.menu.error;
export const selectMenuLastUpdated = (state) => state.menu.lastUpdated;

export default menuSlice.reducer;
